import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import {
  CCard, CCardBody, CButton, CFormInput, CInputGroup,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CSpinner, CAlert, CRow, CCol, CBadge,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilSearch, cilX, cilUserPlus, cilBan, cilPeople } from '@coreui/icons';
import api from '../services/api';
import SortableHeader from '../components/SortableHeader';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { onlineStaff } = useNotifications();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('username');
  const [sortDir, setSortDir] = useState('asc');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function handleSort(col) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  }

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/users', { params: { search, sortBy, sortDir, showInactive: showInactive ? '1' : '0' } });
      setUsers(data.users);
      setTotal(data.total);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortDir, showInactive]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function handleDeactivate(e, id) {
    e.stopPropagation();
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
    } catch {
      setError('Failed to deactivate user.');
    }
  }

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Manage Users</h5>
        </CCol>
        <CCol xs="auto">
          <CButton color="success" size="sm" onClick={() => navigate('/admin/users/new')}>
            <CIcon icon={cilUserPlus} className="me-1" /> Add User
          </CButton>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <form className="d-flex gap-2 mb-3" onSubmit={e => { e.preventDefault(); setSearch(searchInput); }}>
            <CInputGroup>
              <CFormInput
                placeholder="Search by username or name…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <CButton type="submit" color="secondary">
                <CIcon icon={cilSearch} />
              </CButton>
              {search && (
                <CButton color="secondary" variant="outline" onClick={() => { setSearch(''); setSearchInput(''); }}>
                  <CIcon icon={cilX} />
                </CButton>
              )}
            </CInputGroup>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <label className="d-flex align-items-center gap-2 mb-0 small" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                />
                Show inactive
              </label>
            </div>
          </form>

          {error && <CAlert color="danger">{error}</CAlert>}

          {loading ? (
            <div className="text-center py-4"><CSpinner color="success" /></div>
          ) : (
            <>
              <div className="mb-2">
                <small className="text-muted d-flex align-items-center gap-1">
                  <CIcon icon={cilPeople} size="sm" className="text-success" />
                  {search
                    ? <><strong>{total}</strong> result{total !== 1 ? 's' : ''} for "<em>{search}</em>"</>
                    : <><strong>{total}</strong> user{total !== 1 ? 's' : ''}</>}
                </small>
              </div>

              <CTable hover responsive className="mb-0">
                <CTableHead color="light">
                  <CTableRow>
                    <SortableHeader col="username" label="Username" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="nama"     label="Name"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="email"    label="Email"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell>Admin</CTableHeaderCell>
                    <CTableHeaderCell style={{ width: 40 }} title="Google linked"></CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                    <CTableHeaderCell style={{ width: 90 }}></CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {users.map(u => {
                    const isOnline = onlineStaff.some(s => s.username === u.username);
                    return (
                    <CTableRow key={u.user_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${u.user_id}/edit`)}>
                      <CTableDataCell>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span title={isOnline ? 'Online' : 'Offline'} style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#2ecc71' : '#ccc', flexShrink: 0, display: 'inline-block' }} />
                          {u.username}
                        </span>
                      </CTableDataCell>
                      <CTableDataCell>{u.nama}</CTableDataCell>
                      <CTableDataCell>{u.email || <span className="text-muted">—</span>}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={u.isadmin ? 'success' : 'secondary'}>{u.isadmin ? 'Yes' : 'No'}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell title={u.google_email ? `Google: ${u.google_email}` : 'Google not linked'}>
                        {u.google_email ? (
                          <svg viewBox="0 0 24 24" width="18" height="18" style={{ display: 'block' }}>
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="18" height="18" style={{ display: 'block', opacity: 0.18 }}>
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#aaa"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#aaa"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#aaa"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#aaa"/>
                          </svg>
                        )}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={u.isactive ? 'success' : 'danger'}>{u.isactive ? 'Active' : 'Inactive'}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell onClick={e => e.stopPropagation()}>
                        <CButton size="sm" color="success" variant="ghost" className="me-1" onClick={() => navigate(`/admin/users/${u.user_id}/edit`)} title="Edit">
                          <CIcon icon={cilPencil} />
                        </CButton>
                        <CButton size="sm" color="danger" variant="ghost" onClick={e => handleDeactivate(e, u.user_id)} title="Deactivate">
                          <CIcon icon={cilBan} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );})}
                  {users.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={7} className="text-center text-muted py-4">No users found.</CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
